/**
 * The one row shape every step of a coding turn renders as.
 *
 * A coding turn is a long sequence of small, similar steps — a command, a file
 * read, an edit, another command — and the thing that makes such a transcript
 * readable is that every step reads identically: an icon, what was done, what it
 * was done to, and whether it worked. Anything the step produced sits behind the
 * row rather than under it.
 *
 * The label is the action ("Ran command") and the detail is its subject (the
 * command line, the path). They are visually separated — the action carries the
 * weight, the subject stays muted — so a column of these scans as a list of
 * verbs, with the noisy part legible but out of the way.
 *
 * @module components/chat/messages/coding-tool-row
 */

import { CheckIcon, ChevronDownIcon, Loader2, XIcon } from "lucide-react";
import type { ReactNode } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

/** Whether the step is still running, and how it turned out if it is not. */
export type CodingRowState = "running" | "ok" | "error";

export interface CodingToolRowProps {
  /** Leading glyph for the kind of step — a terminal, a file, a magnifier. */
  icon: ReactNode;
  /** The action taken, e.g. "Ran command". Carries the row's weight. */
  label: string;
  /** What it was done to: a command line, a path, a search pattern. */
  detail?: string;
  state: CodingRowState;
  /** Anything the step produced. Absent when there is nothing to expand into. */
  children?: ReactNode;
  "data-testid"?: string;
}

function StateIcon({ state }: { state: CodingRowState }) {
  if (state === "running") {
    return <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />;
  }
  if (state === "error") {
    return <XIcon className="size-3.5 shrink-0 text-destructive" />;
  }
  return <CheckIcon className="size-3.5 shrink-0 text-muted-foreground" />;
}

export function CodingToolRow({
  icon,
  label,
  detail,
  state,
  children,
  "data-testid": testId,
}: CodingToolRowProps) {
  const body = (
    <>
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className="shrink-0 font-medium text-foreground">{label}</span>
      {detail !== undefined && detail.length > 0 && (
        <span className="min-w-0 flex-1 truncate text-start text-muted-foreground">{detail}</span>
      )}
      <span className={cn("ms-auto flex shrink-0 items-center gap-1.5", detail && "ms-0")}>
        {children !== undefined && (
          <ChevronDownIcon
            className={cn(
              "size-3.5 text-muted-foreground/70 transition-transform",
              "group-data-[state=open]/row:rotate-180",
            )}
          />
        )}
        <StateIcon state={state} />
      </span>
    </>
  );

  const row = "flex w-full items-center gap-2 py-1 text-sm";

  if (children === undefined) {
    return (
      <div data-testid={testId} className={row}>
        {body}
      </div>
    );
  }

  return (
    <Collapsible className="not-prose w-full" defaultOpen={false}>
      <CollapsibleTrigger
        data-testid={testId}
        className={cn(row, "group/row rounded-sm transition-colors hover:bg-muted/40")}
      >
        {body}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ms-[1.375rem] border-s border-border ps-3 py-1">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Monospace block for whatever a step produced — output, a path, a patch. */
export function CodingRowOutput({ children }: { children: string }) {
  if (children.length === 0) return null;

  return (
    <pre
      data-testid="coding-row-output"
      className="max-h-72 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-muted-foreground"
    >
      {children}
    </pre>
  );
}
